import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const SOURCE_DISPLAY = { fb: 'Facebook', shopee: 'Shopee', lazada: 'Lazada', tiktok: 'Tiktok', gkash: 'Gkash', checkout: 'Website', admin: 'Admin', woocommerce: 'Website', landingpage: 'Landing Page', offlineret: 'Offline', shopcom: 'Shopcom', 'walk-in': 'Walk-in', walkin: 'Walk-in' };
const normalizeSource = (s) => { if (!s) return s; return SOURCE_DISPLAY[s.toLowerCase()] || s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(); };

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Search, ChevronDown, Loader2, Eye, Plus, UserPlus, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Download, Wrench } from 'lucide-react';
import api from '../services/api';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStore, setSelectedStore] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOrderSource, setSelectedOrderSource] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  // Stores and order sources
  const [stores, setStores] = useState([]);
  const [orderSources, setOrderSources] = useState([]);
  
  // Create client modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '+6',
    email: '',
    location: ''
  });
  const [sortBy, setSortBy] = useState('latest_order'); // 'default', 'latest_order'
  
  // Selection for Fix Stats
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [fixingSelected, setFixingSelected] = useState(false);
  const [fixingAllIds, setFixingAllIds] = useState(false);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Fetch stores and order sources on mount
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [storesRes, sourcesRes] = await Promise.all([
          api.get('/woo-stores'),
          api.get('/clients/order-sources')
        ]);
        setStores(storesRes.data || []);
        setOrderSources(sourcesRes.data || []);
      } catch (e) {
      }
    };
    fetchMeta();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedStore !== 'all' && { store_id: selectedStore }),
        ...(selectedOrderSource !== 'all' && { order_source: selectedOrderSource }),
        ...(sortBy !== 'default' && { sort_by: sortBy })
      };
      const response = await clientsAPI.getAll(params);
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, selectedStatus, selectedStore, selectedOrderSource, sortBy]);

  const handleSearch = () => {
    setPage(1);
    fetchClients();
  };

  const handleStatusChange = async (clientId, newStatus) => {
    try {
      await clientsAPI.update(clientId, { status: newStatus });
      setClients(clients.map(c => 
        c.id === clientId ? { ...c, status: newStatus } : c
      ));
    } catch (error) {
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedClients.size === clients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(clients.map(c => c.id)));
    }
  };

  const handleFixSelected = async () => {
    if (selectedClients.size === 0) return;
    if (!window.confirm(`Fix stats for ${selectedClients.size} selected client(s)? This will recalculate orders, spend, and fix any ID issues.`)) return;
    setFixingSelected(true);
    try {
      await api.post('/clients/fix-stats-selected', { client_ids: [...selectedClients] });
      alert(`Fixing ${selectedClients.size} client(s) in background. Refresh in a few seconds to see results.`);
      setSelectedClients(new Set());
      setTimeout(() => fetchClients(), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error fixing clients');
    }
    setFixingSelected(false);
  };

  const handleFixAllIds = async () => {
    if (!window.confirm('This will fix ALL clients with numeric IDs (migrates to proper UUIDs) and recalculate all order stats. This may take a few minutes for large databases. Continue?')) return;
    setFixingAllIds(true);
    try {
      await api.post('/clients/fix-duplicate-ids');
      alert('ID migration and stats recalculation started in background. Please wait 1-2 minutes then refresh.');
      setTimeout(() => fetchClients(), 5000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
    setFixingAllIds(false);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.phone || !newClient.location) {
      alert('Please fill in all required fields (Name, Phone, Location)');
      return;
    }

    setCreating(true);
    try {
      // Normalize phone number
      const normalizedPhone = newClient.phone.replace(/[^0-9+]/g, '');
      
      await clientsAPI.create({
        ...newClient,
        normalized_phone: normalizedPhone,
        status: 'New',
        orders: 0,
        total_spend: 0
      });
      
      setShowCreateModal(false);
      setNewClient({ name: '', phone: '+6', email: '', location: '' });
      fetchClients();
    } catch (error) {
      alert('Error creating client. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'new':
        return 'bg-blue-100 text-blue-700';
      case 'vip':
        return 'bg-purple-100 text-purple-700';
      case 'inactive':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Import handlers
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportPreview(null);
    setImportResults(null);
    setImportLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/clients/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportPreview(response.data);
    } catch (error) {
      alert('Error reading file. Please check the format.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportExecute = async () => {
    if (!importFile) return;
    
    setImportLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post(
        `/clients/import/execute?overwrite_existing=${overwriteExisting}`, 
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResults(response.data);
      setImportPreview(null);
      fetchClients(); // Refresh the clients list
    } catch (error) {
      alert('Error importing data. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadNotFoundList = () => {
    if (!importResults?.details?.not_found?.length) return;
    
    const csvContent = "Phone,Wallet ID\n" + 
      importResults.details.not_found.map(r => `${r.phone},${r.wallet_id}`).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qpoint_not_found.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Format last order date to show relative time like "4 hours ago", "2 days ago"
  const formatLastOrder = (dateValue) => {
    if (!dateValue || dateValue === '-' || dateValue === 'Never') return 'Never';
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Never';
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
    } catch (e) {
      return 'Never';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-500">{total} total clients{stores.length > 0 ? ` from ${stores.length} store${stores.length > 1 ? 's' : ''}` : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleFixAllIds}
            disabled={fixingAllIds}
            variant="outline"
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="fix-all-ids-btn"
          >
            {fixingAllIds ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wrench size={16} className="mr-2" />}
            Fix All IDs
          </Button>
          <Button 
            onClick={() => {
              setShowImportModal(true);
              setImportFile(null);
              setImportPreview(null);
              setImportResults(null);
              setOverwriteExisting(false);
            }}
            variant="outline"
            className="border-gray-200"
            data-testid="import-qpoint-btn"
          >
            <Upload size={16} className="mr-2" />
            Import Qpoint IDs
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            data-testid="create-client-btn"
          >
            <UserPlus size={16} className="mr-2" />
            Create New Client
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="p-4 bg-white border border-gray-100">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[250px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by name, email, phone, username, or qpoint ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 h-10 border-gray-200"
              data-testid="search-input"
            />
          </div>
          <Button onClick={handleSearch} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10" data-testid="search-btn">
            Search
          </Button>
          <Select value={selectedStore} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-[170px] h-10 border-gray-200" data-testid="store-filter">
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[140px] h-10 border-gray-200" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="VIP">VIP</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedOrderSource} onValueChange={setSelectedOrderSource}>
            <SelectTrigger className="w-[150px] h-10 border-gray-200" data-testid="source-filter">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {orderSources.map(src => (
                <SelectItem key={src} value={src}>{src}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] h-10 border-gray-200" data-testid="sort-filter">
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest_order">Latest Order</SelectItem>
              <SelectItem value="highest_spend">Highest Spend</SelectItem>
              <SelectItem value="most_orders">Most Orders</SelectItem>
              <SelectItem value="newest">Newest Client</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Clients Table */}
      <Card className="bg-white border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              Showing {clients.length} of {total} clients
            </p>
            <div className="flex items-center gap-3">
              {selectedClients.size > 0 && (
                <Button
                  onClick={handleFixSelected}
                  disabled={fixingSelected}
                  variant="outline"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  data-testid="fix-selected-btn"
                >
                  {fixingSelected ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                  Fix Stats ({selectedClients.size})
                </Button>
              )}
              <p className="text-gray-500 text-sm">Page {page} of {Math.ceil(total / 20) || 1}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-3 text-center w-10">
                    <input 
                      type="checkbox" 
                      checked={clients.length > 0 && selectedClients.size === clients.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Spend</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Order</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">First Order</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((client) => (
                  <tr key={client.id} className={`hover:bg-gray-50 transition-colors ${selectedClients.has(client.id) ? 'bg-indigo-50' : ''}`}>
                    <td className="px-3 py-4 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedClients.has(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]"
                        data-testid={`select-client-${client.id}`}
                      />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {client.woo_customer_id || client.crm_id || client.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-4">
                      <div 
                        className="cursor-pointer"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <p className="font-medium text-gray-900 hover:text-[#4F46E5]">{client.name}</p>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{client.phone}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{client.location}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{client.orders}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                      RM{(client.total_spend || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatLastOrder(client.last_order_date || client.last_order)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {client.first_order_date 
                        ? new Date(client.first_order_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '-'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {client.last_order_source ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          {normalizeSource(client.last_order_source)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="flex items-center gap-1">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(client.status)}`}>
                              {client.status}
                            </span>
                            <ChevronDown className="text-gray-400" size={14} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'New')}>
                            New
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'Active')}>
                            Active
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'VIP')}>
                            VIP
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="View Details"
                        data-testid={`view-client-${client.id}`}
                      >
                        <Eye className="text-gray-500 hover:text-[#4F46E5]" size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} clients
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.ceil(total / 20)) }, (_, i) => {
                  const totalPages = Math.ceil(total / 20);
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded text-sm ${
                        page === pageNum
                          ? 'bg-[#4F46E5] text-white'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {Math.ceil(total / 20) > 5 && page < Math.ceil(total / 20) - 2 && (
                  <>
                    <span className="text-gray-400">...</span>
                    <button
                      onClick={() => setPage(Math.ceil(total / 20))}
                      className="w-8 h-8 rounded text-sm hover:bg-gray-100 text-gray-600"
                    >
                      {Math.ceil(total / 20)}
                    </button>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(Math.ceil(total / 20), p + 1))}
                disabled={page >= Math.ceil(total / 20)}
                className="h-8"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Client Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="Enter client name"
                required
                data-testid="new-client-name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone *
              </label>
              <Input
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                placeholder="+60123456789"
                required
                data-testid="new-client-phone"
              />
              <p className="text-xs text-gray-500 mt-1">Malaysia country code (+6) is pre-filled</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="client@example.com"
                data-testid="new-client-email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <Input
                value={newClient.location}
                onChange={(e) => setNewClient({ ...newClient, location: e.target.value })}
                placeholder="e.g., Kuala Lumpur, MY"
                required
                data-testid="new-client-location"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={creating}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="submit-create-client"
              >
                {creating ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <UserPlus size={16} className="mr-2" />
                )}
                Create Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Qpoint IDs Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} />
              Import Qpoint IDs
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload Section */}
            {!importResults && (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="import-file-input"
                  data-testid="import-file-input"
                />
                <label 
                  htmlFor="import-file-input" 
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="text-gray-400 mb-3" size={40} />
                  <p className="font-medium text-gray-700">
                    {importFile ? importFile.name : 'Click to upload Excel or CSV file'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    File should have columns: Phone, Wallet ID (or Qpoint ID)
                  </p>
                </label>
              </div>
            )}

            {/* Loading State */}
            {importLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-[#4F46E5] mr-2" size={24} />
                <span className="text-gray-600">Processing file...</span>
              </div>
            )}

            {/* Preview Section */}
            {importPreview && !importResults && !importLoading && (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="font-semibold text-green-700">Ready to Import</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600 mt-1">{importPreview.matched}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-yellow-600" size={20} />
                      <span className="font-semibold text-yellow-700">Has Qpoint ID</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600 mt-1">{importPreview.has_qpoint}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <XCircle className="text-red-600" size={20} />
                      <span className="font-semibold text-red-700">Not Found</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600 mt-1">{importPreview.not_found}</p>
                  </div>
                </div>

                {/* Columns Detected */}
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <span className="font-medium">Columns detected:</span>{' '}
                  Phone: <span className="text-blue-600">{importPreview.columns_detected?.phone}</span>,{' '}
                  Wallet ID: <span className="text-blue-600">{importPreview.columns_detected?.wallet_id}</span>
                </div>

                {/* Overwrite Option */}
                {importPreview.has_qpoint > 0 && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={overwriteExisting}
                      onChange={(e) => setOverwriteExisting(e.target.checked)}
                      className="rounded"
                    />
                    <span>Overwrite existing Qpoint IDs ({importPreview.has_qpoint} clients)</span>
                  </label>
                )}

                {/* Preview Table */}
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Phone</th>
                        <th className="px-3 py-2 text-left">Wallet ID</th>
                        <th className="px-3 py-2 text-left">Client Name</th>
                        <th className="px-3 py-2 text-left">Existing Qpoint</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {importPreview.preview?.slice(0, 100).map((row, idx) => (
                        <tr key={idx} className={
                          row.status === 'matched' ? 'bg-green-50' :
                          row.status === 'has_qpoint' ? 'bg-yellow-50' : 'bg-red-50'
                        }>
                          <td className="px-3 py-2">
                            {row.status === 'matched' && <CheckCircle className="text-green-600" size={16} />}
                            {row.status === 'has_qpoint' && <AlertCircle className="text-yellow-600" size={16} />}
                            {row.status === 'not_found' && <XCircle className="text-red-600" size={16} />}
                          </td>
                          <td className="px-3 py-2">{row.phone}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.wallet_id?.slice(0, 20)}...</td>
                          <td className="px-3 py-2">{row.client_name || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {row.existing_qpoint ? `${row.existing_qpoint.slice(0, 15)}...` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.preview?.length > 100 && (
                    <p className="text-center text-sm text-gray-500 py-2">
                      Showing 100 of {importPreview.preview.length} rows
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setImportFile(null);
                      setImportPreview(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImportExecute}
                    disabled={importPreview.matched === 0 && !overwriteExisting}
                    className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                    data-testid="execute-import-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    Import {overwriteExisting ? importPreview.matched + importPreview.has_qpoint : importPreview.matched} Clients
                  </Button>
                </div>
              </div>
            )}

            {/* Results Section */}
            {importResults && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <CheckCircle className="text-green-600 mx-auto mb-2" size={40} />
                  <p className="font-semibold text-green-700 text-lg">Import Complete!</p>
                  <p className="text-green-600">
                    Successfully imported {importResults.summary.imported} Qpoint IDs
                    {importResults.summary.overwritten > 0 && ` (${importResults.summary.overwritten} overwritten)`}
                  </p>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-2xl font-bold text-green-600">{importResults.summary.imported}</p>
                    <p className="text-sm text-gray-600">Imported</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-2xl font-bold text-blue-600">{importResults.summary.overwritten}</p>
                    <p className="text-sm text-gray-600">Overwritten</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-2xl font-bold text-yellow-600">{importResults.summary.skipped}</p>
                    <p className="text-sm text-gray-600">Skipped</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-2xl font-bold text-red-600">{importResults.summary.not_found}</p>
                    <p className="text-sm text-gray-600">Not Found</p>
                  </div>
                </div>

                {/* Not Found List with Download */}
                {importResults.details.not_found.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-red-700">
                        {importResults.details.not_found.length} phone numbers not found in system
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={downloadNotFoundList}
                        data-testid="download-not-found-btn"
                      >
                        <Download size={14} className="mr-1" />
                        Download List
                      </Button>
                    </div>
                    <div className="max-h-40 overflow-y-auto bg-gray-50 rounded p-2">
                      {importResults.details.not_found.slice(0, 20).map((item) => (
                        <div key={item.phone} className="text-sm py-1 border-b border-gray-200 last:border-0">
                          <span className="font-mono">{item.phone}</span>
                          <span className="text-gray-400 mx-2">→</span>
                          <span className="text-gray-500 font-mono text-xs">{item.wallet_id.slice(0, 25)}...</span>
                        </div>
                      ))}
                      {importResults.details.not_found.length > 20 && (
                        <p className="text-sm text-gray-500 text-center py-2">
                          ...and {importResults.details.not_found.length - 20} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-2">
                  <Button 
                    onClick={() => setShowImportModal(false)}
                    className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
